import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, unref as _unref, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, vShow as _vShow } from "vue"

const _hoisted_1 = { class: "column" }
const _hoisted_2 = { class: "data" }
const _hoisted_3 = { class: "cell row row--ordered" }
const _hoisted_4 = {
  key: 0,
  class: "cell__body"
}
const _hoisted_5 = ["title"]
const _hoisted_6 = { class: "cell row row--performed" }
const _hoisted_7 = {
  class: /*@__PURE__*/_normalizeClass(['sub-cell'])
}
const _hoisted_8 = { class: "cell row row--expense" }
const _hoisted_9 = {
  class: /*@__PURE__*/_normalizeClass(['sub-cell'])
}
const _hoisted_10 = ["title"]
const _hoisted_11 = {
  class: /*@__PURE__*/_normalizeClass(['cell__header', 'sub-cell'])
}
const _hoisted_12 = {
  key: 0,
  class: "offered-indicator"
}
const _hoisted_13 = {
  key: 0,
  class: "cell__body"
}
const _hoisted_14 = ["title"]
const _hoisted_15 = { class: "cell row row--invoiced-gross" }
const _hoisted_16 = {
  class: /*@__PURE__*/_normalizeClass(['cell__header', 'sub-cell'])
}
const _hoisted_17 = {
  key: 0,
  class: "cell__body"
}
const _hoisted_18 = ["title"]
const _hoisted_19 = { class: "cell row row--invoiced-paid" }
const _hoisted_20 = {
  class: /*@__PURE__*/_normalizeClass(['sub-cell'])
}
const _hoisted_21 = { class: "cell row row--to-pay" }

import { computed } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { formatPrice } from "@/helpers/utils";
import { type WorkingPeriod } from "@/helpers/project/typesDefinition";
import { type Order } from "@/helpers/order/typesDefinition";
import { type Invoice } from "@/helpers/invoice/typesDefinition";
import { type PaginatedResult } from "@/helpers/typesDefinition";
import {
  GET_FINANCIAL_REPORT_BY_MONTH,
  GET_FINANCIAL_REPORT_BY_YEAR,
} from "@/graphql/project/queries";
import dayjs from "dayjs";
import useToggleableTable from "@/composables/UseToggleableTable";
import IconButton from "./IconButton.vue";
import {
  sortedStatusLabels,
  type StatusLabelsKey,
} from "@/helpers/order/utils";

type InvoiceFinancialReport = Pick<
  Invoice,
  | "__typename"
  | "id"
  | "date"
  | "dueDate"
  | "invoiceNumber"
  | "netTotal"
  | "total"
  | "balance"
  | "invoiceStatus"
> & {
  offered?: {
    id: string;
    amountNet: number;
  }[];
};

type OrderFinancialReport = Pick<
  Order,
  | "__typename"
  | "id"
  | "price"
  | "invoiced"
  | "title"
  | "status"
  | "startDate"
  | "endDate"
  | "dueDate"
> & {
  invoice: InvoiceFinancialReport;
};

type ProjectFinancialReport = {
  id: string;
  workingPeriod: WorkingPeriod;
  orders: PaginatedResult<OrderFinancialReport>;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'TableFinancialDataColumn',
  props: {
    projectId: {},
    month: {},
    year: {},
    until: {},
    column: {},
    isFirst: { type: Boolean },
    isLast: { type: Boolean }
  },
  emits: ["clickHeader"],
  setup(__props: any) {

_useCssVars(_ctx => ({
  "c806f7ce": (_ctx.column)
}))

const props = __props;



const { isToggled } = useToggleableTable("financialTable");
const { result } = props.month
  ? useQuery<{ project: ProjectFinancialReport }>(
      GET_FINANCIAL_REPORT_BY_MONTH,
      {
        projectId: props.projectId,
        month: props.month,
      },
    )
  : props.year
  ? useQuery<{ project: ProjectFinancialReport }>(
      GET_FINANCIAL_REPORT_BY_YEAR,
      {
        projectId: props.projectId,
        startDate: dayjs(props.year, "YYYY")
          .startOf("year")
          .format("YYYY-MM-DD"),
        endDate: dayjs(props.year, "YYYY").endOf("year").format("YYYY-MM-DD"),
      },
    ) // props.until
  : useQuery<{ project: ProjectFinancialReport }>(
      GET_FINANCIAL_REPORT_BY_YEAR,
      {
        projectId: props.projectId,
        startDate: "2000-01-01",
        endDate: dayjs(props.until, "YYYY").endOf("year").format("YYYY-MM-DD"),
      },
    );

const workingPeriod = computed(() => result.value?.project.workingPeriod);

const formattedDate = computed(() => {
  return props.month
    ? dayjs(props.month).format("MMMM")
    : props.year
    ? dayjs(props.year).format("YYYY")
    : "Until " + dayjs(props.until).format("YYYY");
});

const invoiced = computed(() => {
  if (!workingPeriod.value) return 0;
  return workingPeriod.value.invoices.reduce(
    ({ gross, net, paid }, i) => ({
      gross: gross + i.total,
      net: net + i.netTotal,
      paid: paid + (i.total - i.balance),
    }),
    { gross: 0, net: 0, paid: 0 },
  );
});

const offeredTotal = computed(() => {
  if (!workingPeriod.value) return 0;
  return workingPeriod.value.invoices.reduce((total, invoice) => {
    return (
      total +
      (invoice.offered ?? []).reduce(
        (sum, offered) => sum + offered.amountNet,
        0,
      )
    );
  }, 0);
});

const invoicedTooltip = computed(() => {
  if (!offeredTotal.value) return undefined;
  return `Incl. offered ${formatPrice(offeredTotal.value)}`;
});

const hasOffered = computed(() => {
  return invoices.value.some(
    (invoice) => invoice.offered && invoice.offered.length > 0,
  );
});

const toPayRaw = computed(() => {
  if (!workingPeriod.value) return 0;
  return workingPeriod.value.invoices.reduce((acc, i) => acc + i.balance, 0);
});

const ordered = computed(() => {
  if (!workingPeriod.value) return 0;
  return workingPeriod.value.orders.reduce((acc, o) => acc + o.price, 0);
});

const formatted = computed(() => {
  if (!invoiced.value || !workingPeriod.value) return {};

  return {
    invoicedGross: formatPrice(invoiced.value.gross),
    invoicedNet: formatPrice(invoiced.value.net),
    invoicedPaid: formatPrice(invoiced.value.paid),
    orderedAmount: formatPrice(ordered.value),
    performedAmount: formatPrice(workingPeriod.value.performedAmount),
    expenseAmount: formatPrice(workingPeriod.value.expenseAmount),
    toPay: formatPrice(toPayRaw.value),
  };
});

const orders = computed(() => workingPeriod.value?.orders ?? []);
const invoices = computed(() => workingPeriod.value?.invoices ?? []);

const isNotApproved = (status: StatusLabelsKey) => {
  const statusIndex = sortedStatusLabels.findIndex((label) => status === label);
  const approvedStatusIndex = sortedStatusLabels.findIndex(
    (label) => label === "approved",
  );

  return statusIndex < approvedStatusIndex;
};

const someOrderNotApproved = computed(() => {
  return orders.value.some((order) => isNotApproved(order.status));
});

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("li", _hoisted_1, [
    _createElementVNode("ul", _hoisted_2, [
      _createElementVNode("li", {
        class: _normalizeClass([
          'cell',
          'cell--header',
          'row',
          'row--header',
          { 'cell--first': __props.isFirst, 'cell--last': __props.isLast },
        ])
      }, [
        (__props.isFirst)
          ? (_openBlock(), _createBlock(IconButton, {
              key: 0,
              "icon-name": "mdi:rewind",
              size: "sm",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('clickHeader')))
            }))
          : _createCommentVNode("", true),
        (__props.isLast)
          ? (_openBlock(), _createBlock(IconButton, {
              key: 1,
              "icon-name": "mdi:fast-forward",
              size: "sm",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('clickHeader')))
            }))
          : _createCommentVNode("", true),
        _createElementVNode("span", null, _toDisplayString(formattedDate.value), 1)
      ], 2),
      _createElementVNode("li", _hoisted_3, [
        _createElementVNode("span", {
          class: _normalizeClass([
            'cell__header',
            'sub-cell',
            { 'sub-cell--warning': someOrderNotApproved.value },
          ])
        }, _toDisplayString(formatted.value.orderedAmount), 3),
        (orders.value.length)
          ? _withDirectives((_openBlock(), _createElementBlock("ul", _hoisted_4, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(orders.value, (order) => {
                return _withDirectives((_openBlock(), _createElementBlock("li", {
                  key: order.id,
                  title: order.title,
                  class: _normalizeClass([
              'sub-cell',
              { 'sub-cell--warning': isNotApproved(order.status) },
            ])
                }, [
                  _createTextVNode(_toDisplayString(_unref(formatPrice)(order.price)), 1)
                ], 10, _hoisted_5)), [
                  [_directive_tooltip, order.title]
                ])
              }), 128))
            ], 512)), [
              [_vShow, props.month && _unref(isToggled)('ordered')]
            ])
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("li", _hoisted_6, [
        _createElementVNode("span", _hoisted_7, _toDisplayString(formatted.value.performedAmount), 1)
      ]),
      _createElementVNode("li", _hoisted_8, [
        _createElementVNode("span", _hoisted_9, _toDisplayString(formatted.value.expenseAmount), 1)
      ]),
      _withDirectives((_openBlock(), _createElementBlock("li", {
        class: "cell row row--invoiced-net",
        title: invoicedTooltip.value
      }, [
        _createElementVNode("span", _hoisted_11, [
          _createTextVNode(_toDisplayString(formatted.value.invoicedNet) + " ", 1),
          (hasOffered.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_12, "*"))
            : _createCommentVNode("", true)
        ]),
        (invoices.value.length)
          ? _withDirectives((_openBlock(), _createElementBlock("ul", _hoisted_13, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(invoices.value, (invoice) => {
                return _withDirectives((_openBlock(), _createElementBlock("li", {
                  key: invoice.id,
                  title: invoice.invoiceNumber,
                  class: _normalizeClass(['sub-cell'])
                }, [
                  _createTextVNode(_toDisplayString(_unref(formatPrice)(invoice.netTotal)), 1)
                ], 8, _hoisted_14)), [
                  [_directive_tooltip, invoice.invoiceNumber]
                ])
              }), 128))
            ], 512)), [
              [_vShow, props.month && _unref(isToggled)('invoicedNet')]
            ])
          : _createCommentVNode("", true)
      ], 8, _hoisted_10)), [
        [_directive_tooltip, invoicedTooltip.value]
      ]),
      _createElementVNode("li", _hoisted_15, [
        _createElementVNode("span", _hoisted_16, _toDisplayString(formatted.value.invoicedGross), 1),
        (invoices.value.length)
          ? _withDirectives((_openBlock(), _createElementBlock("ul", _hoisted_17, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(invoices.value, (invoice) => {
                return _withDirectives((_openBlock(), _createElementBlock("li", {
                  key: invoice.id,
                  title: invoice.invoiceNumber,
                  class: _normalizeClass(['sub-cell'])
                }, [
                  _createTextVNode(_toDisplayString(_unref(formatPrice)(invoice.total)), 1)
                ], 8, _hoisted_18)), [
                  [_directive_tooltip, invoice.invoiceNumber]
                ])
              }), 128))
            ], 512)), [
              [_vShow, props.month && _unref(isToggled)('invoicedGross')]
            ])
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("li", _hoisted_19, [
        _createElementVNode("span", _hoisted_20, _toDisplayString(formatted.value.invoicedPaid), 1)
      ]),
      _createElementVNode("li", _hoisted_21, [
        _createElementVNode("span", {
          class: _normalizeClass(['sub-cell', { 'sub-cell--danger': toPayRaw.value > 0 }])
        }, _toDisplayString(formatted.value.toPay), 3)
      ])
    ])
  ]))
}
}

})